<!-- ANCHOR: FAB -->
@if (isArsonProfile()) {
<div class="chatbot-launcher">
  <div class="chatbot-launcher__row">
    <button
      class="chatbot-fab"
      type="button"
      [class.chatbot-fab--dragging]="isDragging()"
      [attr.aria-label]="'chatbot.aria-open' | translate"
      [attr.aria-expanded]="isOpen()"
      (click)="onFabClick()"
      (pointerdown)="onFabPointerDown($event)"
    >
      @if (isOpen()) {
        <i class="fas fa-times" aria-hidden="true"></i>
      } @else {
        <i class="fas fa-comments" aria-hidden="true"></i>
      }
    </button>
    @if (guideAvailable()) {
      <button
        class="chatbot-guide-tab"
        type="button"
        [attr.aria-label]="'help-panel-open' | translate"
        (click)="openGuide()"
      >
        <i class="fas fa-book-open" aria-hidden="true"></i>
        <span>{{ 'help-guide-available' | translate }}</span>
      </button>
    }
  </div>
</div>

<!-- ANCHOR: Chat Panel -->
<!-- Chat panel — shown only when open -->
@if (isOpen()) {
  <dialog
    class="chatbot-panel"
    open
    [attr.aria-label]="'chatbot.aria-panel' | translate"
    aria-modal="false"
  >
    <!-- ANCHOR: Header -->
    <div class="chatbot-header" (pointerdown)="onHeaderPointerDown($event)">
      <div class="chatbot-header__title">
        <img
          class="chatbot-header__avatar"
          src="assets/chatbot/chatbot-assistant.png"
          alt=""
          aria-hidden="true"
          [style.display]="avatarLoadFailed() ? 'none' : ''"
          (error)="avatarLoadFailed.set(true)"
        />
        <span>{{ chatbotTitle() }}</span>
      </div>
    </div>

    <!-- ANCHOR: Quick Actions Toolbar -->
    <!-- Compact toolbar with structure and instructions shortcuts. -->
    <div class="chatbot-toolbar" role="toolbar" [attr.aria-label]="'chatbot.aria-toolbar' | translate">
      <button
        type="button"
        class="chatbot-toolbar__btn"
        [attr.aria-label]="'help-library-open' | translate"
        (click)="help.openLibrary()"
      >
        <i class="fas fa-book" aria-hidden="true"></i>
        {{ 'help-library-open' | translate }}
      </button>
      <button
        type="button"
        class="chatbot-toolbar__btn"
        (click)="openTree()"
      >
        <i class="fas fa-code-branch" aria-hidden="true"></i>
        {{ 'chatbot.btn-structure' | translate }}
      </button>
      <!-- Opens the read-only intents reference panel — ARSON only -->
      @if (isArsonProfile()) {
        <button
          type="button"
          class="chatbot-toolbar__btn"
          [attr.aria-label]="'chatbot.aria-open-intents' | translate"
          (click)="openInstructions()"
        >
          <i class="fas fa-lightbulb" aria-hidden="true"></i>
          {{ 'chatbot.btn-instructions' | translate }}
        </button>
      }
      <!-- Font size controls: right-aligned, A- decrements, A+ increments (steps 13/15/17/20px) -->
      <div class="chatbot-toolbar__font-controls">
        <button
          type="button"
          class="chatbot-toolbar__btn chatbot-toolbar__btn--icon"
          [disabled]="fontSizeIdx() === 0"
          (click)="decreaseFontSize()"
          title="Reduce text size"
        >
          <span class="chatbot-toolbar__font-label">A<sup>-</sup></span>
        </button>
        <button
          type="button"
          class="chatbot-toolbar__btn chatbot-toolbar__btn--icon"
          [disabled]="fontSizeIdx() === FONT_STEPS.length - 1"
          (click)="increaseFontSize()"
          title="Increase text size"
        >
          <span class="chatbot-toolbar__font-label">A<sup>+</sup></span>
        </button>
      </div>
    </div>

    <!-- ANCHOR: Body — Chat -->
      <!-- ANCHOR: Message List -->
      <div
        class="chatbot-messages"
        #messagesContainer
        role="log"
        aria-live="polite"
        [style.--chatbot-bubble-font-size]="fontSize() + 'px'"
        [attr.aria-label]="'chatbot.aria-history' | translate"
      >
        @for (msg of messages(); track msg.id) {
          <div
            class="chatbot-bubble"
            [attr.data-msg-id]="msg.id"
            [class.chatbot-bubble--user]="msg.role === 'user'"
            [class.chatbot-bubble--bot]="msg.role === 'bot'"
          >
            <p class="chatbot-bubble__text">{{ msg.content }}</p>
            @if (msg.actions; as actions) {
              <div class="chatbot-bubble__actions">
                @for (action of actions; track $index) {
                  <button
                    type="button"
                    class="chatbot-bubble__action"
                    [class.chatbot-bubble__action--primary]="$first"
                    (click)="onMessageAction(action)"
                  >
                    <i class="fas fa-book-open" aria-hidden="true"></i>
                    {{ action.label }}
                  </button>
                }
              </div>
            }
          </div>
        }

        @if (isTyping()) {
          <div
            class="chatbot-bubble chatbot-bubble--bot chatbot-bubble--typing"
            [attr.aria-label]="'chatbot.aria-typing' | translate"
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        }
      </div>

      <!-- ANCHOR: Input Area -->
      <div class="chatbot-input">
        <input
          #chatInput
          type="text"
          class="chatbot-input__field"
          [placeholder]="'chatbot.placeholder' | translate"
          [attr.aria-label]="'chatbot.aria-input' | translate"
          autocomplete="off"
          [value]="inputText()"
          (input)="inputText.set(getInputValue($event))"
          (keydown)="onKeydown($event)"
        />
        <button
          type="button"
          class="chatbot-input__send"
          [attr.aria-label]="'chatbot.aria-send' | translate"
          [disabled]="!canSend()"
          (click)="send()"
        >
          <i class="fas fa-paper-plane" aria-hidden="true"></i>
        </button>
        <!-- Clear chat messages -->
        <button
          type="button"
          class="chatbot-input__clear"
          [attr.aria-label]="'chatbot.aria-clear-chat' | translate"
          (click)="clearChat()"
          title="Clear chat"
        >
          <i class="fas fa-trash-alt" aria-hidden="true"></i>
        </button>
      </div>

  </dialog>
}

<!-- ANCHOR: App Tree Dialog (full-screen, rendered outside the chatbot panel) -->
@if (isTreeOpen()) {
  <app-chatbot-app-tree
    (closed)="closeTree()"
    (navigate)="navigateFromTree($event)"
  ></app-chatbot-app-tree>
}

<!-- ANCHOR: Instructions Dialog (full-screen, rendered outside the chatbot panel) -->
@if (isArsonProfile() && isInstructionsOpen()) {
  <app-chatbot-instructions
    (closed)="closeInstructionsDialog()"
    (exampleClicked)="onExampleClicked($event)"
  ></app-chatbot-instructions>
}
} <!-- end @if (isArsonProfile()) -->

